import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

const TIER_COLOR = {
  Master: "#f4b658",
  Journeyman: "#c5a059",
  Apprentice: "#b49b64",
  Novice: "#8c826e",
  Uninitiated: "#6e6a62",
};

const TIER_ORDER = ["Master", "Journeyman", "Apprentice", "Novice"];

export default function Leaderboard() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/leaderboard?limit=30");
        setEntries(data.entries || []);
      } catch (ex) {
        setErr(ex?.response?.data?.detail || "Failed to load leaderboard");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const grouped = TIER_ORDER.map((tier) => ({
    tier,
    items: entries.filter((e) => e.rank?.name === tier),
  })).filter((g) => g.items.length > 0);

  return (
    <div data-testid="leaderboard-page" style={{ padding: "60px 6vw 100px", maxWidth: 1200, margin: "0 auto" }}>
      <div className="overline" style={{ marginBottom: 14 }}>§ The Lyceum · Leaderboard</div>
      <h1 className="font-serif" style={{ fontSize: "clamp(40px, 5vw, 64px)", lineHeight: 1, marginBottom: 10 }}>
        Those who have <span className="font-display" style={{ color: "var(--brass)" }}>endured</span>.
      </h1>
      <p className="font-serif" style={{ color: "var(--parchment-dim)", fontStyle: "italic", fontSize: 17, marginBottom: 40 }}>
        Public sprints, ranked by the Aristotelian tier earned through the Six Disciplines and the Chaos Test.
      </p>

      <div className="rule-parchment" style={{ marginBottom: 40 }} />

      {loading && <div className="overline" data-testid="leaderboard-loading">Consulting the Codex...</div>}
      {err && <div className="font-mono" style={{ color: "var(--warning)" }}>{err}</div>}

      {!loading && !err && entries.length === 0 && (
        <div data-testid="leaderboard-empty" className="glass" style={{ padding: 60, textAlign: "center" }}>
          <div className="overline" style={{ marginBottom: 12 }}>§ Awaiting the first initiate</div>
          <h3 className="font-serif" style={{ fontSize: 28, margin: "0 0 10px", fontStyle: "italic" }}>
            No public sprints yet.
          </h3>
          <p className="font-serif" style={{ color: "var(--parchment-dim)", fontSize: 15 }}>
            Complete a sprint and make its Seal public to claim the first rank.
          </p>
          <Link to="/laboratory" className="btn-brass" style={{ marginTop: 24, display: "inline-block", padding: "12px 28px", textDecoration: "none" }}>
            Begin a Sprint
          </Link>
        </div>
      )}

      {grouped.map(({ tier, items }) => (
        <section key={tier} style={{ marginBottom: 56 }} data-testid={`leaderboard-tier-${tier.toLowerCase()}`}>
          <div
            className="font-display"
            style={{
              fontSize: 28, color: TIER_COLOR[tier], marginBottom: 20, letterSpacing: "0.05em",
              display: "flex", alignItems: "baseline", gap: 14,
            }}
          >
            <span>✦</span>
            <span style={{ fontStyle: "italic" }}>{tier}</span>
            <span className="font-mono" style={{ fontSize: 11, color: "var(--muted)", letterSpacing: "0.25em" }}>
              {items.length}
            </span>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: 2,
              background: "rgba(197,160,89,0.14)",
              border: "1px solid rgba(197,160,89,0.14)",
            }}
          >
            {items.map((e, idx) => (
              <Link
                key={e.project_id}
                to={`/seal/${e.project_id}`}
                target="_blank"
                rel="noreferrer"
                data-testid={`leaderboard-entry-${e.project_id}`}
                className="reveal"
                style={{
                  background: "var(--ink-surface)",
                  padding: 24,
                  textDecoration: "none",
                  color: "inherit",
                  display: "flex", flexDirection: "column", gap: 10,
                  minHeight: 200,
                  animationDelay: `${0.04 * idx}s`,
                  borderTop: `2px solid ${TIER_COLOR[tier]}`,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span
                    className="font-mono"
                    style={{ fontSize: 10, letterSpacing: "0.25em", color: TIER_COLOR[tier], textTransform: "uppercase" }}
                  >
                    #{idx + 1}
                  </span>
                  <span className="font-mono" style={{ fontSize: 10, color: "var(--parchment-dim)" }}>
                    {e.views} view{e.views === 1 ? "" : "s"} · {e.shares} share{e.shares === 1 ? "" : "s"}
                  </span>
                </div>
                <h4 className="font-serif" style={{ fontSize: 22, margin: 0, lineHeight: 1.2 }}>
                  {e.name}
                </h4>
                <p className="font-mono" style={{ fontSize: 11, color: "var(--parchment-dim)", lineHeight: 1.5, flex: 1, margin: 0 }}>
                  {e.description || "—"}
                </p>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginTop: "auto" }}>
                  <span className="font-mono" style={{ fontSize: 10, color: "var(--muted)", letterSpacing: "0.15em", textTransform: "uppercase" }}>
                    by {e.owner_name}
                  </span>
                  <span className="font-mono" style={{ fontSize: 10, color: e.chaos_passed ? "var(--success)" : "var(--warning)" }}>
                    ✓ {e.chaos_pass} probes
                  </span>
                  {e.deployed && (
                    <span className="font-mono" style={{ fontSize: 10, color: "var(--brass)", letterSpacing: "0.15em" }}>
                      · DEPLOYED
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
